// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Layout from 'antd/lib/layout';

import GlobalHotKeys, { KeyMap } from 'utils/mousetrap-react';
import { ActiveControl, Job, Rotation } from 'reducers';
import { Canvas } from 'cvat-canvas-wrapper';

import RotateControl from 'components/annotation-page/standard-workspace/controls-side-bar/rotate-control';
import CursorControl from 'components/annotation-page/standard-workspace/controls-side-bar/cursor-control';
import MoveControl from 'components/annotation-page/standard-workspace/controls-side-bar/move-control';
import FitControl from 'components/annotation-page/standard-workspace/controls-side-bar/fit-control';
import ResizeControl from 'components/annotation-page/standard-workspace/controls-side-bar/resize-control';
import { JobStage } from 'cvat-core/src/enums';
import IssueControl from './issue-control';
import ConfirmFrameInspectionControl from './confirm-frame-inspection-control';

interface Props {
    jobInstance: Job;
    frame: number;
    canvasInstance: Canvas;
    activeControl: ActiveControl;
    keyMap: KeyMap;
    normalizedKeyMap: Record<string, string>;
    frameIsDeleted: boolean;

    rotateFrame(rotation: Rotation): void;
    selectIssuePosition(enabled: boolean): void;
}

export default function ControlsSideBarComponent(props: Props): JSX.Element {
    const {
        jobInstance, frame,
        canvasInstance, activeControl, normalizedKeyMap, keyMap, frameIsDeleted,
        rotateFrame, selectIssuePosition,
    } = props;

    const controlsDisabled = frameIsDeleted;

    const preventDefault = (event: KeyboardEvent | undefined): void => {
        if (event) {
            event.preventDefault();
        }
    };

    const subKeyMap = {
        CANCEL: keyMap.CANCEL,
        OPEN_REVIEW_ISSUE: keyMap.OPEN_REVIEW_ISSUE,
    };

    let handlers: any = {
        CANCEL: (event: KeyboardEvent | undefined) => {
            preventDefault(event);
            if (activeControl !== ActiveControl.CURSOR) {
                canvasInstance.cancel();
            }
        },
    };

    if (!controlsDisabled) {
        handlers = {
            ...handlers,
            OPEN_REVIEW_ISSUE: (event: KeyboardEvent | undefined) => {
                preventDefault(event);
                if (activeControl === ActiveControl.OPEN_ISSUE) {
                    canvasInstance.selectRegion(false);
                    selectIssuePosition(false);
                } else {
                    canvasInstance.cancel();
                    canvasInstance.selectRegion(true);
                    selectIssuePosition(true);
                }
            },
        };
    }

    return (
        <Layout.Sider className='cvat-canvas-controls-sidebar' theme='light' width={44}>
            <GlobalHotKeys keyMap={subKeyMap} handlers={handlers} />
            <CursorControl
                cursorShortkey={normalizedKeyMap.CANCEL}
                canvasInstance={canvasInstance}
                activeControl={activeControl}
            />
            <MoveControl canvasInstance={canvasInstance} activeControl={activeControl} />
            <RotateControl
                anticlockwiseShortcut={normalizedKeyMap.ANTICLOCKWISE_ROTATION}
                clockwiseShortcut={normalizedKeyMap.CLOCKWISE_ROTATION}
                rotateFrame={rotateFrame}
            />

            <hr />

            <FitControl canvasInstance={canvasInstance} />
            <ResizeControl canvasInstance={canvasInstance} activeControl={activeControl} />

            <hr />
            <IssueControl
                canvasInstance={canvasInstance}
                activeControl={activeControl}
                selectIssuePosition={selectIssuePosition}
                disabled={controlsDisabled}
            />

            {[JobStage.INSPECTION].includes(jobInstance.stage) ? (
                <>
                    <hr />
                    <ConfirmFrameInspectionControl
                        jobInstance={jobInstance}
                        frame={frame}
                        disabled={controlsDisabled}
                    />
                </>
            ) : null}

        </Layout.Sider>
    );
}
